import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { LOCALE_ID, NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { Ability, PureAbility } from '@casl/ability';
import { MissingTranslationHandler, TranslateCompiler, TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { OAuthModule } from 'angular-oauth2-oidc';
import { PhraseAppCompiler } from 'ngx-translate-phraseapp';
import { SCIPApiModule, SCIPConfiguration, SCIPConfigurationParameters } from '@scip/api';
import { AppComponent } from './app.component';
import { AppRouting } from './app.routing';
import { ScipMenuComponent } from './scip.menu.component';
import { ScipTopBarComponent } from './scip.topbar.component';
import { CoreModule } from './scip/core/core.module';
import { ScipMissingTranslationHandler } from './scip/core';
import { ProductsModule } from './scip/pages/products-page';
import { ScipErrorPageComponent } from './scip/scip-error-page';
import { AuthInterceptor, EnvConfigServiceProvider, EnvironmentConfigurationService } from './scip/services';
import { SharedModule } from './scip/shared';
import { VendorModule } from './vendor.module';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { environment } from '../environments/environment';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { AvanoNotificationsModule } from '@avano/avano-components/avano-notifications';
import { NotificationsService } from './scip/services/notifications.service';
import { TableSettingsService } from '@avano/avano-components/avano-table/avano-table-settings.service';
import { ScipTableDbSettingsService } from './scip/services/scip-table-db-settings.service';
import { ScipValidationErrorsService } from './scip/services/avano/scip-validation-errors-service';
import { ExampleValidationErrorsService } from '@avano/avano-components/avano-validation/validation-errors-service';
import { LoggerModule, NGXLogger, NgxLoggerLevel } from 'ngx-logger';
import { ROOT_STATE, metaReducers } from './scip/store/index';
import { SubmissionsPageEffects } from './scip/store/submissions-page';
import { ProductsPageEffects } from './scip/store/products-page';
import { ScipConfigurationService } from './scip/services/scip-configuration.service';

// import ngx-translate and the http loader
@NgModule({
  imports: [
    BrowserModule,
    RouterModule,
    OAuthModule.forRoot(),
    FormsModule,
    AppRouting,
    HttpClientModule,
    BrowserAnimationsModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient, EnvironmentConfigurationService]
      },
      compiler: {
        provide: TranslateCompiler,
        useClass: PhraseAppCompiler
      },
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useClass: ScipMissingTranslationHandler,
        deps: [NGXLogger]
      }
    }),
    CoreModule,
    SharedModule,
    VendorModule,
    ProductsModule,
    StoreModule.forRoot(ROOT_STATE, {
      metaReducers,
      runtimeChecks: {
        // strictStateImmutability and strictActionImmutability are enabled by default
        //TODO: take a look at the error, when strict serializabiliaty flags are set to true
        // strictStateSerializability: true,
        // strictActionSerializability: true,
        // strictActionWithinNgZone: true,
        strictActionTypeUniqueness: true
      }
    }),
    EffectsModule.forRoot([SubmissionsPageEffects, ProductsPageEffects]),
    !environment.production ? StoreDevtoolsModule.instrument() : [],
    StoreRouterConnectingModule.forRoot(),
    AvanoNotificationsModule.forRoot(NotificationsService),
    LoggerModule.forRoot({
      level: NgxLoggerLevel.LOG,
      disableConsoleLogging: false,
      enableSourceMaps: true,
      serverLogLevel: NgxLoggerLevel.ERROR
    })
  ],
  declarations: [AppComponent, ScipTopBarComponent, ScipMenuComponent, ScipErrorPageComponent],
  providers: [
    EnvConfigServiceProvider,
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: Ability, useValue: new Ability() },
    { provide: PureAbility, useExisting: Ability },
    { provide: LOCALE_ID, useValue: 'en-US' },
    {
      provide: TableSettingsService,
      useClass: ScipTableDbSettingsService
    },
    {
      provide: ExampleValidationErrorsService, // TODO: Ask Viktor if that is on purpose xD
      useClass: ScipValidationErrorsService
    },
    {
      provide: SCIPConfiguration,
      useClass: ScipConfigurationService
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {}

export function HttpLoaderFactory(http: HttpClient, env: EnvironmentConfigurationService): TranslateHttpLoader {
  // Adding a query param forces the browser to not use the cached version of the file.
  // As the query parameter changes per version, within one version the file is then still cached.
  return new TranslateHttpLoader(http, env.environment.translationsUri, '.json?v=' + env.environment.scipVersion);
}
